import { useState } from "react";
import { useChitChatProfile } from "@/hooks/useChitChatProfile";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CcAvatar, AfroDivider } from "./primitives";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export function ChitChatOnboarding({ onDone }: { onDone: () => void }) {
  const { user } = useAuth();
  const { upsert } = useChitChatProfile();
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState(user?.user_metadata?.full_name || "");
  const [bio, setBio] = useState("");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!user) return;
    if (!username.trim() || !displayName.trim()) {
      toast({ title: "Username and display name are required", variant: "destructive" });
      return;
    }
    setBusy(true);
    let avatar_url: string | null = null;
    if (avatarFile) {
      const path = `${user.id}/avatar-${Date.now()}-${avatarFile.name}`;
      const { error: upErr } = await supabase.storage
        .from("chitchat-avatars")
        .upload(path, avatarFile, { upsert: true });
      if (!upErr) {
        const { data } = supabase.storage.from("chitchat-avatars").getPublicUrl(path);
        avatar_url = data.publicUrl;
      }
    }
    const { error } = await upsert({
      username: username.trim().toLowerCase(),
      display_name: displayName.trim(),
      bio: bio.trim() || null,
      avatar_url,
    });
    setBusy(false);
    if (error) {
      toast({ title: "Could not save profile", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Welcome to ChitChat 🌍", description: "Your profile is ready." });
    onDone();
  };

  return (
    <div className="chitchat-theme min-h-[80vh] flex items-center justify-center p-4 chitchat-pattern">
      <div className="chitchat-surface w-full max-w-md p-6 shadow-lg">
        <div className="flex flex-col items-center gap-3">
          <div className="h-20 w-20 rounded-full chitchat-brand-bg flex items-center justify-center text-2xl font-bold shadow-md">
            CC
          </div>
          <h1 className="text-2xl font-bold text-cc-ink">Karibu ChitChat</h1>
          <p className="text-sm text-cc-ink-soft text-center">
            Set up your ChitChat identity. This is how friends will find and recognise you.
          </p>
        </div>
        <AfroDivider className="my-5" />

        <div className="flex items-center gap-3 mb-4">
          <CcAvatar
            name={displayName || "?"}
            id={user?.id}
            src={avatarFile ? URL.createObjectURL(avatarFile) : undefined}
            size={64}
          />
          <div className="flex-1">
            <Label className="text-xs text-cc-ink-soft">Profile photo</Label>
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => setAvatarFile(e.target.files?.[0] ?? null)}
            />
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <Label htmlFor="cc-username">Username</Label>
            <Input
              id="cc-username"
              value={username}
              onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_.]/g, ""))}
              placeholder="e.g. amani.k"
              maxLength={32}
            />
          </div>
          <div>
            <Label htmlFor="cc-display">Display name</Label>
            <Input
              id="cc-display"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={48}
            />
          </div>
          <div>
            <Label htmlFor="cc-bio">About</Label>
            <Textarea
              id="cc-bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="A short line about you"
              maxLength={140}
              rows={2}
            />
          </div>
        </div>

        <Button
          className="chitchat-btn-primary w-full mt-5 h-11"
          disabled={busy}
          onClick={submit}
        >
          {busy ? "Saving..." : "Enter ChitChat"}
        </Button>
      </div>
    </div>
  );
}
